import type { AdapterCapabilities, AdapterSchemas } from './skill-platform-types';
import { AdapterConfig } from './skill-platform-types';
import {
  BEESWAX_MEDIA_PLATFORM_ID,
  FACEBOOK_MEDIA_PLATFORM_ID,
  GOOGLE_SEARCH_MEDIA_PLATFORM_ID
} from 'shared/src/domain/media-platform/media-platforms';
import {
  beeswaxAdapterConfig,
  beeswaxAdapterSchemas,
  beeswaxCapabilities
} from './beeswax/shared-config';
import {
  googleAdsAdapterConfig,
  googleAdsAdapterSchemas,
  googleAdsCapabilities
} from './google-ads/shared-config';
import { metaAdapterConfig, metaAdapterSchemas, metaCapabilities } from './meta/shared-config';

// The frontend-safe per-platform projection: config (incl. the workflowSteps UI metadata), schemas,
// and capabilities. No LLM prompt (backend-only, now in each skill's doc) and no backend facets.
export type SkillPlatformConfig = {
  platformId: number;
  config: AdapterConfig;
  schemas: AdapterSchemas;
  capabilities: AdapterCapabilities;
};

// Parsing at registration enforces the AdapterConfig cross-field invariants for every platform.
const registry: Record<number, SkillPlatformConfig> = {
  [BEESWAX_MEDIA_PLATFORM_ID]: {
    platformId: BEESWAX_MEDIA_PLATFORM_ID,
    config: AdapterConfig.parse(beeswaxAdapterConfig),
    schemas: beeswaxAdapterSchemas,
    capabilities: beeswaxCapabilities
  },
  [GOOGLE_SEARCH_MEDIA_PLATFORM_ID]: {
    platformId: GOOGLE_SEARCH_MEDIA_PLATFORM_ID,
    config: AdapterConfig.parse(googleAdsAdapterConfig),
    schemas: googleAdsAdapterSchemas,
    capabilities: googleAdsCapabilities
  },
  [FACEBOOK_MEDIA_PLATFORM_ID]: {
    platformId: FACEBOOK_MEDIA_PLATFORM_ID,
    config: AdapterConfig.parse(metaAdapterConfig),
    schemas: metaAdapterSchemas,
    capabilities: metaCapabilities
  }
};

export function getSkillPlatformConfig(platformId: number): SkillPlatformConfig {
  const entry = registry[platformId];
  if (!entry) {
    throw new Error(`No skill platform config registered for platform_id=${platformId}`);
  }
  return entry;
}

export function hasSkillPlatformConfig(platformId: number): boolean {
  return platformId in registry;
}
